import { useState } from "react";
import axios from "axios";
import { photosApi } from "../services/api";
import { FileWithPreview } from "@contexts/index";
import pLimit from "p-limit";
import { computeETag } from "src/utils/hashing.utils";
import { uploadTimeoutMs } from "src/components/order/new/utils/upload-utils";
import { captureSentryExceptionOnce } from "./sentry";

// NOTE: the live customer upload path is startUploadWithOrderPhoto in
// src/components/order/new/utils/upload-utils.ts. This hook is still mounted by
// the new-order page (for its progress/error state) but `startUpload` below is
// no longer called by anything. Kept in sync with the live path on the two
// points that used to lose photos: a size-proportional PUT timeout, and an
// ETag mismatch that reports instead of throwing.

const limit = pLimit(3); // Adjust the concurrency limit as needed

interface NestJSError {
  statusCode: number;
  message: string | string[];
  error: string;
}

interface UploadBatch {
  files: File[];
  counts: string;
  ranks: string;
}

// Added OrderPhotoUploadParams to support uploading to OrderPhoto
export interface OrderPhotoUploadParams {
  orderId: string;
  orderPhotoId?: string; // Optional: if provided, upload to specific OrderPhoto
}

export const useUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingBuffer, setUploadingBuffer] = useState(0);
  const [uploadError, setUploadError] = useState<NestJSError | null>(null);
  const MAX_RETRY_ATTEMPTS = 3;

  // Function to get presigned URLs and upload files to MinIO
  const uploadBatchToMinio = async (
    files: File[]
  ): Promise<{ key: string; name: string; type: string }[]> => {
    try {
      const fileInfos = files.map((file) => ({
        name: file.name,
        type: file.type,
      }));
      const response = await photosApi.photoControllerGetPresignedUrls({
        files: fileInfos,
      });

      const { urls } = response.data;

      if (urls === undefined || urls.length !== files.length) {
        throw new Error("Failed to get presigned URLs for all files");
      }

      // Upload each file using the presigned URL
      await Promise.all(
        files.map((file, index) =>
          limit(async () => {
            const { url, headUrl, key } = urls[index];
            if (!url || !headUrl) {
              throw new Error("Presigned URL is missing");
            }
            await uploadFileWithRetry(url, headUrl, file);
          })
        )
      );

      return urls.map((urlObj, index) => ({
        key: urlObj.key || "",
        type: files[index].type,
        name: files[index].name,
      }));
    } catch (error) {
      console.error("Failed to upload batch to MinIO:", error);
      console.log("Files:", files);
      const filesData = files.map((file) => ({
        name: file.name,
        type: file.type,
      }));
      console.log("Files data: ", filesData);
      captureSentryExceptionOnce(error, {});
      throw new Error("Failed to upload batch to MinIO");
    }
  };

  // Function to upload a batch with retries
  // Updated to support orderPhotoId
  const uploadBatch = async (
    filesBatch: File[],
    countsBatch: string,
    ranksBatch: string,
    params: OrderPhotoUploadParams,
    attempt = 1
  ): Promise<void> => {
    try {
      const keys = await uploadBatchToMinio(filesBatch);

      // Upload files using presigned URLs and metadata      const keys = await uploadBatchToMinio(filesBatch);
      console.log("OrderPhotoId: ", params.orderPhotoId);
      const metadata = keys.map((key, index) => ({
        key: key.key,
        type: key.type,
        name: key.name,
        count: JSON.parse(countsBatch)[index],
        rank: JSON.parse(ranksBatch)[index],
        orderPhotosId: params.orderPhotoId, // Add orderPhotoId to metadata (note: API expects "orderPhotosId" with an 's')
      }));

      // Use the updated endpoint that supports OrderPhoto
      await photosApi.photoControllerUploadMetadataBatch({ metadata });
    } catch (error: unknown) {
      if (attempt < MAX_RETRY_ATTEMPTS) {
        console.log(`Retrying upload... Attempt: ${attempt}`);
        return await uploadBatch(
          filesBatch,
          countsBatch,
          ranksBatch,
          params,
          attempt + 1
        );
      } else {
        console.error("Failed to upload batch after maximum attempts:", error);
        console.log("Files:", filesBatch);
        console.log("Counts:", countsBatch);
        console.log("Ranks:", ranksBatch);
        const filesData = filesBatch.map((file) => ({
          name: file.name,
          type: file.type,
        }));
        console.log("Files data: ", filesData);
        captureSentryExceptionOnce(error, {});
        throw error;
      }
    }
  };

  async function uploadFileWithRetry(
    url: string,
    headUrl: string,
    file: File,
    retries = 4
  ) {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const response = await axios.put(url, file, {
          headers: {
            "Content-Type": file.type,
          },
          timeout: uploadTimeoutMs(file.size),
        });

        // Advisory integrity check. A re-read of a File on iOS Safari and the
        // in-app browsers can return shifted bytes, so a mismatch here is
        // almost always a client re-read artifact; the size check below is the
        // guard that actually catches truncation. Throwing on the hash dropped
        // photos that were sitting fine in MinIO.
        try {
          const computedETag = await computeETag(file);
          const serverETag = response.headers.etag?.replace(/"/g, ""); // Strip quotes
          if (serverETag && computedETag !== serverETag) {
            console.warn(
              `ETag mismatch (advisory, keeping upload): ${computedETag} !== ${serverETag}`
            );
          }
        } catch (hashErr) {
          console.warn("ETag compute failed (advisory, keeping upload)", hashErr);
        }

        // Perform a HEAD request to verify file size
        const headResponse = await axios.head(headUrl, {
          headers: { "Content-Type": file.type },
        });

        const remoteFileSize = parseInt(
          String(headResponse.headers["content-length"]),
          10
        );

        if (remoteFileSize !== file.size) {
          console.error(
            `Size mismatch: expected ${file.size} bytes but got ${remoteFileSize} bytes`
          );
          throw new Error("Uploaded file size does not match original size");
        }

        return; // Exit function if upload is successful and verified
      } catch (error) {
        if (attempt === retries) {
          if (axios.isAxiosError(error)) {
            console.error(
              `Failed to upload file ${file.name} after ${retries} attempts`,
              {
                status: error.response?.status,
                data: error.response?.data,
                headers: error.response?.headers,
                file: file.name,
              }
            );
          } else if (error instanceof Error) {
            console.error(
              `Failed to upload file ${file.name}: ${error.message}`
            );
          } else {
            console.error(`Failed to upload file ${file.name}: Unknown error`);
          }

          throw error;
        }
        console.warn(
          `Retrying upload for ${file.name}, attempt ${attempt + 1}`
        );
        captureSentryExceptionOnce(error, {
          tags: { fileName: file.name },
          extra: { url, fileType: file.type },
        });
        await new Promise((res) => setTimeout(res, attempt * 1000)); // Exponential backoff
      }
    }
  }

  // Function to upload batches concurrently
  const uploadBatchesConcurrently = async (
    batches: UploadBatch[],
    params: OrderPhotoUploadParams,
    totalFiles: number,
    uploadedFilesCount: number,
    setProgressCallback: (progress: number) => void
  ): Promise<number> => {
    let updatedUploadedFilesCount = uploadedFilesCount;
    try {
      await Promise.all(
        batches.map(async (batch) => {
          try {
            await uploadBatch(batch.files, batch.counts, batch.ranks, params);
          } catch (error) {
            console.log("Error in uploading batch: ", error);
            throw error;
          }
          // Update uploaded file count and progress
          updatedUploadedFilesCount += batch.files.length;
          const progress = (updatedUploadedFilesCount / totalFiles) * 100;
          setProgressCallback(progress);
        })
      );

      return updatedUploadedFilesCount;
    } catch (error: unknown) {
      console.error("Failed to upload batches concurrently:", error);
      captureSentryExceptionOnce(error, {});
      throw error;
    }
  };

  // Main function to start the upload process - updated to support uploading to OrderPhoto
  const startUpload = async (
    files: FileWithPreview[],
    params: OrderPhotoUploadParams,
    concurrentUploadLimit: number = 3,
    batchUploadSize: number = 3
  ): Promise<void> => {
    setUploading(true);
    setUploadError(null);

    const counts = files.map((file) => file.count ?? 1);
    const ranks = files.map((_, index) => index);
    const totalFiles = files.length;

    let uploadedFiles = 0;
    let uploadedFilesBuffer = 0;

    for (
      let i = 0;
      i < totalFiles;
      i += batchUploadSize * concurrentUploadLimit
    ) {
      const end = i + batchUploadSize * concurrentUploadLimit;
      const batches: UploadBatch[] = [];

      for (let j = i; j < end; j += batchUploadSize) {
        const filesBatch = files
          .slice(j, j + batchUploadSize)
          .map((file) => file.file);
        if (filesBatch.length === 0) break;

        const countsBatch = JSON.stringify(
          counts.slice(j, j + batchUploadSize)
        );
        const ranksBatch = JSON.stringify(ranks.slice(j, j + batchUploadSize));

        batches.push({
          files: filesBatch,
          counts: countsBatch,
          ranks: ranksBatch,
        });
      }

      try {
        // Set buffer progress before uploading
        uploadedFilesBuffer += Math.min(end - i, totalFiles - i);
        setUploadingBuffer((uploadedFilesBuffer / totalFiles) * 100);
        console.log(
          "Uploading buffer progress: ",
          (uploadedFilesBuffer / totalFiles) * 100
        );
        // Upload batches concurrently and update progress
        uploadedFiles = await uploadBatchesConcurrently(
          batches,
          params,
          totalFiles,
          uploadedFiles,
          (progress) => setUploadProgress(progress)
        );
      } catch (error: unknown) {
        const e = error as {
          response?: { status?: number; data?: { message?: string; error?: string } };
        };
        setUploadError({
          statusCode: e.response?.status || 500,
          message: e.response?.data?.message || "Unknown error occurred",
          error: e.response?.data?.error || "Internal Server Error",
        });

        captureSentryExceptionOnce(error, {});
        break;
      }
    }

    setUploading(false);
  };

  return {
    uploading,
    uploadProgress,
    uploadingBuffer,
    uploadError,
    startUpload,
  };
};
